import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { useAuth } from '@/auth/AuthProvider'
import { FxAvatar, FxButton, FxIcon, FxTextField } from '@/components/fx'
import { initialsOf } from '@/layouts/FunnelLayout'
import { ApiError, authApi, usersApi } from '@/lib/api'
import {
  hasErrors,
  isUnchanged,
  profilePatch,
  validateProfile,
  type ProfileDraft,
  type ProfileErrors,
} from './profileForm'

/**
 * Name, e-mail and the confirmation state, as `Einstellungen.dc.html` draws
 * them: the big avatar beside a two-column grid, the e-mail read-only with
 * its pill inside the field.
 *
 * The e-mail is read-only because there is no endpoint that changes it —
 * `PATCH /v1/users/me` takes names and a picture, and an editable field that
 * silently discards what is typed is worse than a locked one.
 */
export function ProfileSection() {
  const { user, reload } = useAuth()

  const stored: ProfileDraft = {
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
  }
  const [draft, setDraft] = useState<ProfileDraft>(stored)
  const [errors, setErrors] = useState<ProfileErrors>({})

  // The profile arrives after the first render (the bootstrap `/me`), and
  // again after a save. Keyed on the stored values rather than on `user`, so
  // an unrelated profile refresh cannot throw away what is being typed.
  const storedKey = `${stored.firstName}|${stored.lastName}`
  useEffect(() => {
    setDraft({ firstName: stored.firstName, lastName: stored.lastName })
    setErrors({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedKey])

  const save = useMutation({
    mutationFn: async (next: ProfileDraft) => {
      await usersApi.patchMe(profilePatch(next))
      // The rail's avatar and the account menu read the profile from the
      // auth context, so the new name has to land there before this returns.
      await reload()
    },
  })

  const email = user?.email ?? ''
  const initials = initialsOf({ ...draft, email }) || '?'
  const unchanged = isUnchanged(draft, stored)

  function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    const found = validateProfile(draft)
    setErrors(found)
    if (hasErrors(found)) return
    save.mutate(draft)
  }

  return (
    // `noValidate` keeps the browser's own bubble out of the way: the fields
    // stay `required` for assistive technology, and the message under the
    // field is ours, in German, in the design's wording.
    <form noValidate onSubmit={onSubmit} className="flex flex-col gap-9 md:flex-row md:items-start">
      <FxAvatar
        initials={initials}
        aria-hidden
        className="h-24 w-24 self-start text-[38px] md:h-[200px] md:w-[200px] md:text-[64px]"
      />
      <div className="grid flex-grow gap-6 sm:grid-cols-2 md:gap-x-9">
        <FxTextField
          label="Vorname"
          required
          value={draft.firstName}
          autoComplete="given-name"
          error={errors.firstName}
          onChange={(event) => setDraft((d) => ({ ...d, firstName: event.target.value }))}
        />
        <FxTextField
          label="Nachname"
          required
          value={draft.lastName}
          autoComplete="family-name"
          error={errors.lastName}
          onChange={(event) => setDraft((d) => ({ ...d, lastName: event.target.value }))}
        />
        <EmailField email={email} verified={user?.emailVerified} />
        <div className="flex items-end">
          <FxButton type="submit" block disabled={save.isPending || unchanged}>
            {save.isPending ? 'Wird gespeichert …' : 'Änderungen speichern'}
          </FxButton>
        </div>
        <p aria-live="polite" className="text-small sm:col-span-2">
          {save.isSuccess && !save.isPending && (
            <span className="text-success-text">Ihre Angaben wurden gespeichert.</span>
          )}
          {save.isError && (
            <span className="text-danger-text">
              {ApiError.from(save.error).code === 'network_error'
                ? 'Keine Verbindung zum Server. Bitte später erneut versuchen.'
                : 'Ihre Angaben konnten nicht gespeichert werden. Bitte später erneut versuchen.'}
            </span>
          )}
        </p>
      </div>
    </form>
  )
}

/**
 * The e-mail row: the address, and the truth about it.
 *
 * `emailVerified` is `undefined` only against a gateway older than today's,
 * and a pill that claimed either state from no data would be a guess — so
 * that case shows the address alone.
 */
function EmailField({ email, verified }: { email: string; verified?: boolean }) {
  return (
    <div className="flex flex-col gap-2 sm:col-span-2 md:col-span-1">
      <FxTextField
        label="E-Mail"
        value={email}
        readOnly
        // The pill is positioned inside the field and FxTextField only
        // reserves 40px for it, which "nicht bestätigt" overruns.
        className={
          verified === undefined
            ? undefined
            : verified
              ? 'pr-28 text-text-muted'
              : 'pr-40 text-text-muted'
        }
        trailing={
          verified === undefined ? null : verified ? (
            <span className="flex items-center gap-1 text-small text-success-text">
              <FxIcon name="verified" size={18} />
              bestätigt
            </span>
          ) : (
            <span className="flex items-center gap-1 text-small text-warning-text">
              <FxIcon name="error" size={18} />
              nicht bestätigt
            </span>
          )
        }
      />
      {verified === false && <ResendVerification />}
    </div>
  )
}

/**
 * The confirmation mail, resent from the place somebody goes looking for it.
 *
 * The gateway allows five per hour per account and answers `429
 * resend_rate_limited`. That is information, not a failure: someone who
 * clicks twice has done nothing wrong, so the limit is said in words and the
 * button stops rather than showing an error.
 */
function ResendVerification() {
  const resend = useMutation({
    mutationFn: () => authApi.resendVerification(),
    onError: () => {},
  })
  const limited = resend.isError && ApiError.from(resend.error).code === 'resend_rate_limited'
  const failed = resend.isError && !limited

  return (
    <div className="flex flex-wrap items-center gap-4">
      <FxButton
        variant="outlined"
        icon="mail"
        disabled={resend.isPending || resend.isSuccess || limited}
        onClick={() => resend.mutate()}
      >
        Bestätigung senden
      </FxButton>
      <span aria-live="polite" className="text-small text-text-muted">
        {resend.isSuccess && 'Verschickt. Bitte sehen Sie auch im Spam-Ordner nach.'}
        {limited &&
          'In der letzten Stunde wurden bereits fünf Bestätigungen verschickt. Bitte in einer Stunde noch einmal versuchen.'}
        {failed && 'Die Bestätigung konnte gerade nicht verschickt werden.'}
      </span>
    </div>
  )
}
